import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const inputCls =
  "w-full bg-[#161622] border border-[#242436] focus:border-[#8B5CF6] outline-none text-[#F4F4F6] text-sm px-4 py-3.5 placeholder:text-[#62627A] transition-colors duration-200";

const empty = { name: "", email: "", subject: "", message: "", company: "" };

export const ContactForm = () => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent!", { description: "We'll get back to you soon." });
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="py-20 sm:py-28 lg:py-36 px-4 sm:px-8 lg:px-16" data-testid="contact-section">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
        <Reveal className="lg:col-span-2">
          <div className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-[#FFB800] mb-4">Get In The Game</div>
          <h2 className="font-display font-black uppercase tracking-tight leading-none text-3xl sm:text-4xl lg:text-5xl text-[#F4F4F6]">
            Talk to <span className="text-outline">us.</span>
          </h2>
          <div className="mt-8 space-y-5">
            <div className="flex items-center gap-4">
              <Mail className="w-5 h-5 text-[#8B5CF6] shrink-0" />
              <a href="mailto:jmizell0057@gmail.com" data-testid="contact-email-link" className="font-mono text-sm text-[#A1A1B3] hover:text-[#FFB800] transition-colors">jmizell0057@gmail.com</a>
            </div>
            <div className="flex items-center gap-4">
              <Phone className="w-5 h-5 text-[#8B5CF6] shrink-0" />
              <a href="tel:+14782313286" data-testid="contact-phone-link" className="font-mono text-sm text-[#A1A1B3] hover:text-[#FFB800] transition-colors">(478) 231-3286</a>
            </div>
            <div className="flex items-center gap-4">
              <MapPin className="w-5 h-5 text-[#8B5CF6] shrink-0" />
              <span className="font-mono text-sm text-[#A1A1B3]">Serving school-age youth in our local community</span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-3">
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="contact-form">
            <input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
            <input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
            <input placeholder="Subject" value={form.subject} onChange={set("subject")} className={`${inputCls} sm:col-span-2`} data-testid="contact-subject-input" />
            <input tabIndex="-1" autoComplete="off" value={form.company} onChange={set("company")} className="hidden" aria-hidden="true" data-testid="contact-company-honeypot" />
            <textarea required placeholder="Your message *" value={form.message} onChange={set("message")} rows={5} className={`${inputCls} sm:col-span-2 resize-none`} data-testid="contact-message-input" />
            <button
              type="submit"
              disabled={busy}
              data-testid="contact-form-submit"
              className="sm:col-span-2 border border-[#8B5CF6] text-[#8B5CF6] font-display font-extrabold uppercase tracking-wider text-base px-8 py-4 flex items-center justify-center gap-2 hover:bg-[#8B5CF6] hover:text-black active:scale-[0.98] disabled:opacity-50 transition-all duration-200"
            >
              <Send className="w-4 h-4" />
              {busy ? "Sending..." : "Send Message"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
